import React, { useEffect, useMemo, useState } from "react";
import { ArtifactProto } from "build-ts/chat_session";
import WorkflowRunCardComponent, { TAB, TabId, isValidTabId } from "../WorkflowRunCard";
import { ScheduledJobProto } from "build-ts/schedule";
import { HeaderWithStats } from "services/MetadataServices";
import useRunAuthorNames from "hooks/useRunAuthorNames";

interface WorkflowRunCardContainerProps {
  artifact: ArtifactProto;
  isExpanded?: boolean;
  onExpandToggle?: () => void;
  defaultTimeZone?: string;
  /** Restored tab from the persisted view state. */
  initialActiveTab?: string;
  /** Restored output pill name from the persisted view state. */
  initialOutputPillName?: string;
  /** Restored input pill name from the persisted view state. */
  initialInputPillName?: string;
  /** Called whenever tab or pill selection changes so the parent can persist it. */
  onArtifactTabViewChange?: (view: {
    activeTab: string;
    selectedOutputPillName: string;
    selectedInputPillName?: string;
  }) => void;
  /** When true, hides the artifact header (used in WorkflowRuns page). */
  hideHeader?: boolean;
  /** Analyst list, for analyst name lookup */
  analystList?: HeaderWithStats[];
  /** Scenario jobs, for owner ID lookup */
  scenarioJobs?: ScheduledJobProto[];
  /** Author display name */
  authorName?: string;
}

/**
 * Container for WorkflowRun artifacts.
 * Owns tab and pill selection state; syncs from props so the parent can
 * persist and restore view state when the user navigates between panel tabs.
 */
const WorkflowRunCardContainer: React.FC<WorkflowRunCardContainerProps> = ({
  artifact,
  isExpanded,
  onExpandToggle,
  defaultTimeZone = "",
  initialActiveTab,
  initialOutputPillName,
  initialInputPillName,
  onArtifactTabViewChange,
  hideHeader = false,
  analystList,
  scenarioJobs,
  authorName,
}) => {
  const [activeTab, setActiveTab] = useState<TabId>(isValidTabId(initialActiveTab) ? initialActiveTab : TAB.outputs);
  const [selectedOutputPillName, setSelectedOutputPillName] = useState(initialOutputPillName ?? "");
  const [selectedInputPillName, setSelectedInputPillName] = useState(initialInputPillName ?? "");

  useEffect(() => {
    if (isValidTabId(initialActiveTab)) setActiveTab(initialActiveTab);
  }, [initialActiveTab]);

  useEffect(() => {
    if (initialOutputPillName !== undefined) setSelectedOutputPillName(initialOutputPillName);
  }, [initialOutputPillName]);

  useEffect(() => {
    if (initialInputPillName !== undefined) setSelectedInputPillName(initialInputPillName);
  }, [initialInputPillName]);

  const handleTabChange = (tab: TabId) => {
    setActiveTab(tab);
    onArtifactTabViewChange?.({ activeTab: tab, selectedOutputPillName, selectedInputPillName });
  };

  const handleOutputPillChange = (name: string) => {
    setSelectedOutputPillName(name);
    onArtifactTabViewChange?.({ activeTab, selectedOutputPillName: name, selectedInputPillName });
  };

  const handleInputPillChange = (name: string) => {
    setSelectedInputPillName(name);
    onArtifactTabViewChange?.({ activeTab, selectedOutputPillName, selectedInputPillName: name });
  };

  const runId = artifact.workflow_run?.header?.id;
  // Skip the fetch when authorName is already provided by the parent to avoid a redundant fetchChatSession call.
  const runRefs = useMemo(
    () =>
      !authorName && runId && artifact.workflow_run?.source_chat_id
        ? [{ runId, sourceChatId: artifact.workflow_run.source_chat_id }]
        : [],
    [authorName, runId, artifact.workflow_run?.source_chat_id]
  );
  const runAuthorNames = useRunAuthorNames(runRefs);
  const resolvedAuthorName = authorName ?? (runId ? runAuthorNames[runId] : undefined);

  return (
    <WorkflowRunCardComponent
      artifact={artifact}
      activeTab={activeTab}
      selectedOutputPillName={selectedOutputPillName}
      selectedInputPillName={selectedInputPillName}
      onTabChange={handleTabChange}
      onOutputPillChange={handleOutputPillChange}
      onInputPillChange={handleInputPillChange}
      isExpanded={isExpanded}
      onExpandToggle={onExpandToggle}
      defaultTimeZone={defaultTimeZone}
      hideHeader={hideHeader}
      analystList={analystList}
      scenarioJobs={scenarioJobs}
      authorName={resolvedAuthorName}
    />
  );
};

export default WorkflowRunCardContainer;
